import React, { useState } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import CostSheetDashboard from './CostSheetDashboard';
import CostSheetForm from './CostSheetForm';
import CostSheetResult from './CostSheetResult';
import CostSheetComparison from './CostSheetComparison';
import CostSheetList from './CostSheetList';

const CostSheet = ({ onBack, currentUser }) => {
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' | 'create' | 'list' | 'result' | 'compare' | 'view'
  const [currentSheet, setCurrentSheet] = useState(null);
  const [calculatedData, setCalculatedData] = useState(null);
  const [originalSheetForComparison, setOriginalSheetForComparison] = useState(null);

  const handleNavigate = (view) => {
    setCurrentView(view);
    if (view === 'create') {
      setCurrentSheet(null);
      setCalculatedData(null);
    }
  };

  const handleCalculate = (data) => {
    setCalculatedData(data);
    setCurrentView('result');
  };

  const handleSave = async (data) => {
    try {
      const sheetData = {
        ...data,
        createdAt: serverTimestamp(),
        createdBy: {
          uid: currentUser.uid,
          displayName: currentUser.displayName
        }
      };

      await addDoc(collection(db, 'costSheets'), sheetData);
      alert('Cost sheet saved successfully!');
      setCurrentView('dashboard');
      setCurrentSheet(null);
      setCalculatedData(null);
    } catch (error) {
      console.error('Error saving cost sheet:', error);
      alert('Failed to save cost sheet. Please try again.');
    }
  };

  const handleCompare = () => {
    // Store original sheet data for comparison
    setOriginalSheetForComparison({
      ...calculatedData,
      createdAt: { toDate: () => new Date() }
    });
    setCurrentView('compare');
  };

  const handleViewSheet = (sheet) => {
    setCurrentSheet(sheet);
    setCalculatedData({
      ...sheet,
      volume: sheet.volume,
      calculatedWeight: sheet.calculatedWeight,
      effectiveWeight: sheet.effectiveWeight,
      interestDays: sheet.interestDays,
      interestAmount: sheet.interestAmount,
      shippingCost: sheet.shippingCost,
      exchangeRate: sheet.exchangeRate
    });
    setCurrentView('result');
  };

  const handleBackFromComparison = () => {
    setCurrentView('result');
    setOriginalSheetForComparison(null);
  };

  // Render based on current view
  switch (currentView) {
    case 'dashboard':
      return <CostSheetDashboard onNavigate={handleNavigate} onBack={onBack} />;

    case 'create':
      return (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => handleNavigate('dashboard')}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title="Back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold">Create New Cost Sheet</h1>
            </div>
            <CostSheetForm
              onCalculate={handleCalculate}
              showResults={false}
            />
          </div>
        </div>
      );

    case 'result':
      return (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => handleNavigate('create')}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title="Back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold">Cost Sheet Results</h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <CostSheetForm
                  initialValues={calculatedData}
                  onCalculate={handleCalculate}
                  onSubmit={handleSave}
                  showResults={true}
                />
              </div>
              <div className="lg:col-span-1">
                {calculatedData && (
                  <CostSheetResult
                    formData={calculatedData}
                    calculatedValues={{
                      volume: calculatedData.volume,
                      calculatedWeight: calculatedData.calculatedWeight,
                      effectiveWeight: calculatedData.effectiveWeight,
                      interestDays: calculatedData.interestDays,
                      interestAmount: calculatedData.interestAmount,
                      shippingCost: calculatedData.shippingCost
                    }}
                    exchangeRate={calculatedData.exchangeRate}
                    onCompare={handleCompare}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );

    case 'compare':
      return (
        <CostSheetComparison
          originalSheet={originalSheetForComparison}
          onBack={handleBackFromComparison}
        />
      );

    case 'list':
      return (
        <CostSheetList
          onView={handleViewSheet}
          onBack={() => handleNavigate('dashboard')}
          currentUser={currentUser}
        />
      );

    default:
      return <CostSheetDashboard onNavigate={handleNavigate} onBack={onBack} />;
  }
};

export default CostSheet;

